import { Vec3 } from '../math/Vec3';
import type { SimulationConfig } from '../simulation/types';
import { DEFAULT_CONFIG } from './defaults';

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('La configuración debe ser un objeto JSON.');
  return value as Record<string, unknown>;
}

export function parseConfig(input: unknown, base: SimulationConfig = DEFAULT_CONFIG): SimulationConfig {
  const data = record(input);
  const allowed = new Set([...Object.keys(DEFAULT_CONFIG), 'schemaVersion', 'units']);
  for (const key of Object.keys(data)) {
    if (!allowed.has(key)) throw new Error(`Campo desconocido: ${key}`);
  }
  if (data.schemaVersion !== undefined && data.schemaVersion !== 1) throw new Error('Versión de configuración no compatible.');
  if (data.units !== undefined && data.units !== 'metres') throw new Error('Las unidades deben ser metres.');
  const merged = { ...base, ...data } as Record<string, unknown>;
  const number = (key: string, min: number, max: number, integer = false): number => {
    const value = merged[key];
    if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max || (integer && !Number.isInteger(value))) {
      throw new Error(`${key}: introduce ${integer ? 'un entero' : 'un número'} entre ${min} y ${max}.`);
    }
    return value;
  };
  const choice = <T extends string>(key: string, choices: readonly T[]): T => {
    const value = merged[key];
    if (typeof value !== 'string' || !choices.includes(value as T)) throw new Error(`${key}: valor no compatible.`);
    return value as T;
  };
  const cube = record(merged.cubeSize);
  for (const axis of ['x', 'y', 'z']) {
    if (typeof cube[axis] !== 'number' || !Number.isFinite(cube[axis]) || cube[axis] < 0.01 || cube[axis] > 10) {
      throw new Error(`cubeSize.${axis}: debe estar entre 0.01 y 10 metros.`);
    }
  }
  if (typeof merged.occlusionEnabled !== 'boolean') throw new Error('occlusionEnabled debe ser booleano.');
  const result: SimulationConfig = {
    cubeSize: new Vec3(cube.x as number, cube.y as number, cube.z as number),
    meshCount: number('meshCount', 1, 10000, true),
    meshDensity: number('meshDensity', 3, 32, true),
    minimumMeshDistance: number('minimumMeshDistance', 0, 0.1),
    filamentRadius: number('filamentRadius', 0.000001, 0.01),
    meshPattern: choice('meshPattern', ['radial', 'cartesian', 'triangular', 'voronoi', 'random']),
    meshStrategy: choice('meshStrategy', ['parallel', 'pseudo-random', 'optimized']),
    orientationRandomness: number('orientationRandomness', 0, Math.PI),
    curvature: number('curvature', 0, 1),
    logicalLaserCount: number('logicalLaserCount', 1, 10000, true),
    renderedRayLimit: number('renderedRayLimit', 1, 10000, true),
    hemisphereRadius: number('hemisphereRadius', 0.01, 10),
    laserMinAngle: number('laserMinAngle', 0, Math.PI / 2 + 1e-10),
    laserMaxAngle: number('laserMaxAngle', 0, Math.PI / 2 + 1e-10),
    // Legacy files may contain false; physical first-hit calibration is mandatory.
    occlusionEnabled: true,
    scatteringCoefficient: number('scatteringCoefficient', 0, 1),
    projectionFigure: choice('projectionFigure', ['HEART', 'SPHERE', 'CUBE', 'HELIX', 'STAR', 'TORUS', 'DNA', 'PYRAMID']),
    projectionTolerance: number('projectionTolerance', 0.0001, 0.2),
    seed: number('seed', 0, 0xffffffff, true),
  };
  if (result.laserMinAngle > result.laserMaxAngle) throw new Error('El ángulo mínimo no puede superar el máximo.');
  return result;
}
